<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { locationTypesApi } from '@/modules/settings/services/locationTypesApi'
import type { HospitalLocationType } from '@/modules/settings/types/hospitalStructure.types'

interface Props {
  isOpen: boolean
  locationType?: HospitalLocationType | null
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void
  (e: 'saved', value: HospitalLocationType): void
}

const props = withDefaults(defineProps<Props>(), {
  locationType: null,
})

const emit = defineEmits<Emits>()

const { smAndDown } = useDisplay()
const feedbackStore = useFeedbackStore()

const formRef = ref()
const isSaving = ref(false)
const name = ref('')
const description = ref('')
const canHaveAddress = ref(false)

const isEditMode = computed(() => !!props.locationType?.id)
const title = computed(() => (isEditMode.value ? 'Редактировать тип помещения' : 'Добавить тип помещения'))
const submitLabel = computed(() => (isEditMode.value ? 'Сохранить' : 'Создать'))

const rules = {
  name: [
    (v: string) => !!v?.trim() || 'Наименование обязательно',
    (v: string) => (v?.trim().length ?? 0) <= 100 || 'Не больше 100 символов',
  ],
}

function resetForm() {
  name.value = props.locationType?.name ?? ''
  description.value = props.locationType?.description ?? ''
  canHaveAddress.value = Boolean(props.locationType?.can_have_address)
}

function closeModal() {
  if (isSaving.value) {
    return
  }
  emit('update:isOpen', false)
}

watch(
  () => props.isOpen,
  async open => {
    if (!open) {
      return
    }
    resetForm()
    await nextTick()
    formRef.value?.resetValidation()
  },
)

async function onSubmit() {
  const { valid } = await formRef.value.validate()
  if (!valid) {
    return
  }

  isSaving.value = true
  try {
    const payload = {
      name: name.value.trim(),
      description: description.value.trim(),
      can_have_address: canHaveAddress.value,
    }
    const saved = isEditMode.value && props.locationType
      ? await locationTypesApi.update(props.locationType.id, payload)
      : await locationTypesApi.create(payload)

    feedbackStore.showToast({
      type: 'success',
      message: isEditMode.value ? 'Тип помещения сохранён' : 'Тип помещения создан',
    })
    emit('saved', saved)
    emit('update:isOpen', false)
  } catch (caught) {
    feedbackStore.showToast({
      type: 'error',
      message: getApiError(
        caught,
        isEditMode.value ? 'Не удалось сохранить тип помещения' : 'Не удалось создать тип помещения',
      ),
    })
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <VDialog
    :model-value="isOpen"
    :max-width="smAndDown ? '100%' : 560"
    :fullscreen="smAndDown"
    persistent
    @update:model-value="emit('update:isOpen', $event)"
  >
    <VCard :class="smAndDown ? 'pa-4' : 'pa-6'">
      <div class="d-flex justify-space-between align-center mb-4">
        <span class="text-h5 font-weight-bold">{{ title }}</span>
        <CustomCloseButton :close-action="closeModal" />
      </div>

      <VForm ref="formRef" @submit.prevent="onSubmit">
        <VTextField
          v-model="name"
          label="Наименование"
          :rules="rules.name"
          class="mb-4"
        />
        <VTextarea
          v-model="description"
          label="Описание"
          rows="3"
          class="mb-4"
        />
        <VSwitch
          v-model="canHaveAddress"
          label="Может иметь адрес"
          color="primary"
          class="mb-6"
          hide-details
        />
        <div
          class="d-flex ga-3"
          :class="smAndDown ? 'flex-column' : 'justify-end'"
        >
          <VBtn
            variant="outlined"
            color="secondary"
            :block="smAndDown"
            :disabled="isSaving"
            @click="closeModal"
          >
            Отмена
          </VBtn>
          <VBtn
            type="submit"
            color="primary"
            :block="smAndDown"
            :loading="isSaving"
          >
            {{ submitLabel }}
          </VBtn>
        </div>
      </VForm>
    </VCard>
  </VDialog>
</template>
